import React, { useState, useEffect } from 'react';
import { API_LINK } from '../../apiLink/apiLink';
import Layout from '../../components/Layout';

const StudentMarksReview = ({ onNavigate, userInfo }) => {
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 1024);
  const [activeTab, setActiveTab] = useState('marks-review');

  // API Data States
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [successMessage, setSuccessMessage] = useState(null);
  const [submitError, setSubmitError] = useState(null);

  // Marks Review Data
  const [marksReviewData, setMarksReviewData] = useState({
    availableSemesters: [],
    subjectsBySemester: {},
    failedSubjectsBySemester: {},
    previousReviewRequests: [],
    previousSupplyApplications: []
  });

  // Marks Review Form State
  const [reviewForm, setReviewForm] = useState({
    semester: '',
    academicYear: '',
    subject: '',
    reason: ''
  });

  // Supply Exam Form State
  const [supplyForm, setSupplyForm] = useState({
    semester: '',
    academicYear: '',
    failedSubjects: []
  });

  // Check if mobile on mount and resize
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth <= 1024);
    };

    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  // Fetch Data from API
  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        setError(null);

        const token = localStorage.getItem('token') || sessionStorage.getItem('token');

        if (!token) {
          setError('Authentication token not found. Please login again.');
          setLoading(false);
          return;
        }

        // Fetch marks review data
        const marksReviewResponse = await fetch(`${API_LINK}/api/student/marks-review-data`, {
          method: 'GET',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        });

        if (!marksReviewResponse.ok) {
          if (marksReviewResponse.status === 401) {
            setError('Session expired. Please login again.');
            window.location.href = '/';
            return;
          } else if (marksReviewResponse.status === 404) {
            setError('No academic records found.');
            setLoading(false);
            return;
          }
          throw new Error(`HTTP error! status: ${marksReviewResponse.status}`);
        }

        const marksReviewResult = await marksReviewResponse.json();

        if (marksReviewResult.success && marksReviewResult.data) {
          setMarksReviewData(marksReviewResult.data);
          console.log('✅ Marks review data loaded successfully');
        } else {
          setError(marksReviewResult.message || 'Failed to fetch marks review data');
        }

      } catch (error) {
        console.error('💥 Error fetching data:', error);
        setError(`Failed to load data: ${error.message}`);

        setError(`Failed to load data: ${error.message}`);
      } finally {
        setLoading(false);
      }
    };

    const timer = setTimeout(fetchData, 500);
    return () => clearTimeout(timer);
  }, [userInfo]);

  // Update failed subjects when supply form semester/year changes
  useEffect(() => {
    if (supplyForm.semester && supplyForm.academicYear) {
      const key = `${supplyForm.semester}_${supplyForm.academicYear}`;
      const failedSubjects = marksReviewData.failedSubjectsBySemester[key] || [];
      // Reset selected subjects when semester/year changes
      setSupplyForm(prev => ({ ...prev, failedSubjects: [] }));
    }
  }, [supplyForm.semester, supplyForm.academicYear, marksReviewData]);



  // Handle marks review form submission
  const handleReviewSubmit = async (e) => {
    e.preventDefault();

    if (!reviewForm.semester || !reviewForm.academicYear || !reviewForm.reason.trim()) {
      setSubmitError('Please fill in all required fields');
      return;
    }

    if (reviewForm.reason.trim().length < 10) {
      setSubmitError('Please provide a detailed reason (minimum 10 characters)');
      return;
    }

    try {
      setSubmitting(true);
      setSubmitError(null);
      setSuccessMessage(null);

      const token = localStorage.getItem('token') || sessionStorage.getItem('token');

      if (!token) {
        setSubmitError('Authentication required');
        return;
      }

      const response = await fetch(`${API_LINK}/api/student/marks-review-request`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          semester: reviewForm.semester,
          academicYear: reviewForm.academicYear,
          subject: reviewForm.subject || null,
          reason: reviewForm.reason.trim()
        })
      });

      const result = await response.json();

      if (response.ok && result.success) {
        setSuccessMessage(result.message);
        setReviewForm({ semester: '', academicYear: '', subject: '', reason: '' });

        // Refresh data to show new request
        setTimeout(() => {
          window.location.reload();
        }, 2000);
      } else {
        setSubmitError(result.message || 'Failed to submit review request');
      }

    } catch (error) {
      console.error('Error submitting review request:', error);
      setSubmitError('Failed to submit request. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  // Handle supply exam form submission
  const handleSupplySubmit = async (e) => {
    e.preventDefault();

    if (!supplyForm.semester || !supplyForm.academicYear || supplyForm.failedSubjects.length === 0) {
      setSubmitError('Please select semester, academic year, and at least one failed subject');
      return;
    }

    try {
      setSubmitting(true);
      setSubmitError(null);
      setSuccessMessage(null);

      const token = localStorage.getItem('token') || sessionStorage.getItem('token');

      if (!token) {
        setSubmitError('Authentication required');
        return;
      }

      const response = await fetch(`${API_LINK}/api/student/supply-exam-application`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          semester: supplyForm.semester,
          academicYear: supplyForm.academicYear,
          failedSubjects: supplyForm.failedSubjects
        })
      });

      const result = await response.json();

      if (response.ok && result.success) {
        setSuccessMessage(result.message);
        setSupplyForm({ semester: '', academicYear: '', failedSubjects: [] });

        // Refresh data to show new application
        setTimeout(() => {
          window.location.reload();
        }, 2000);
      } else {
        setSubmitError(result.message || 'Failed to submit supply exam application');
      }

    } catch (error) {
      console.error('Error submitting supply application:', error);
      setSubmitError('Failed to submit application. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  // Handle failed subject selection
  const handleFailedSubjectToggle = (subjectCode) => {
    setSupplyForm(prev => ({
      ...prev,
      failedSubjects: prev.failedSubjects.includes(subjectCode)
        ? prev.failedSubjects.filter(s => s !== subjectCode)
        : [...prev.failedSubjects, subjectCode]
    }));
  };

  // Get available failed subjects for current semester/year
  const getAvailableFailedSubjects = () => {
    if (!supplyForm.semester || !supplyForm.academicYear) return [];
    const key = `${supplyForm.semester}_${supplyForm.academicYear}`;
    return marksReviewData.failedSubjectsBySemester[key] || [];
  };

  // Get available subjects for current semester
  const getAvailableSubjects = () => {
    if (!reviewForm.semester) return [];
    return marksReviewData.subjectsBySemester[reviewForm.semester] || [];
  };

  // Format date
  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-GB', {
      day: '2-digit',
      month: 'short',
      year: 'numeric'
    });
  };

  // Loading Component
  if (loading) {
    return (
      <div style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        height: '100vh',
        backgroundColor: '#f4f7fa',
        flexDirection: 'column',
        gap: '1rem'
      }}>
        <div style={{
          width: '50px',
          height: '50px',
          border: '4px solid #e3f2fd',
          borderTop: '4px solid #238548',
          borderRadius: '50%',
          animation: 'spin 1s linear infinite'
        }}></div>
        <p style={{ color: '#666', fontSize: '1.1rem' }}>Loading Marks Review...</p>
        <style>
          {`
            @keyframes spin {
              0% { transform: rotate(0deg); }
              100% { transform: rotate(360deg); }
            }
          `}
        </style>
      </div>
    );
  }

  // Error Component
  if (error) {
    return (
      <div style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        height: '100vh',
        backgroundColor: '#f4f7fa',
        flexDirection: 'column',
        gap: '1rem',
        padding: '2rem'
      }}>
        <div style={{
          backgroundColor: '#ffffff',
          padding: '3rem',
          borderRadius: '12px',
          boxShadow: '0 4px 15px rgba(0,0,0,0.1)',
          textAlign: 'center',
          maxWidth: '400px',
          border: '1px solid #e0e0e0'
        }}>
          <div style={{
            fontSize: '4rem',
            color: '#ff6b6b',
            marginBottom: '1.5rem'
          }}>
            ⚠️
          </div>

          <h3 style={{
            color: '#333',
            margin: '0 0 1rem 0',
            fontSize: '1.4rem',
            fontWeight: 600
          }}>
            Unable to Load Marks Review
          </h3>

          <p style={{
            color: '#666',
            margin: '0 0 2rem 0',
            fontSize: '1rem',
            lineHeight: 1.5
          }}>
            {error}
          </p>

          <div style={{
            display: 'flex',
            gap: '1rem',
            justifyContent: 'center',
            flexWrap: 'wrap'
          }}>
            <button
              onClick={() => window.location.reload()}
              style={{
                backgroundColor: '#238548',
                color: 'white',
                border: 'none',
                padding: '0.75rem 1.5rem',
                borderRadius: '8px',
                cursor: 'pointer',
                fontSize: '1rem',
                fontWeight: 500
              }}
            >
              Try Again
            </button>

            <button
              onClick={() => window.location.href = '/'}
              style={{
                backgroundColor: '#666',
                color: 'white',
                border: 'none',
                padding: '0.75rem 1.5rem',
                borderRadius: '8px',
                cursor: 'pointer',
                fontSize: '1rem',
                fontWeight: 500
              }}
            >
              Logout
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <Layout role="student"
      onNavigate={onNavigate}
      activePage="student-marks-review"
      pageTitle="Marks Review & Supply Exam"
    >

      {/* Success/Error Messages */}
      {(successMessage || submitError) && (
        <div style={{
          background: successMessage ? '#d4edda' : '#f8d7da',
          border: `1px solid ${successMessage ? '#c3e6cb' : '#f5c6cb'}`,
          color: successMessage ? '#155724' : '#721c24',
          padding: '1rem',
          borderRadius: '8px',
          marginBottom: '1.5rem',
          display: 'flex',
          alignItems: 'center',
          gap: '0.5rem'
        }}>
          <span style={{ fontSize: '1.2rem' }}>
            {successMessage ? '✅' : '❌'}
          </span>
          {successMessage || submitError}
          <button
            onClick={() => {
              setSuccessMessage(null);
              setSubmitError(null);
            }}
            style={{
              marginLeft: 'auto',
              background: 'none',
              border: 'none',
              color: 'inherit',
              cursor: 'pointer',
              fontSize: '1.2rem'
            }}
          >
            ✕
          </button>
        </div>
      )}

      {/* Page Header */}
      <div style={{
        background: '#ffffff',
        borderRadius: '12px',
        boxShadow: '0 4px 15px rgba(0,0,0,0.1)',
        marginBottom: '2rem',
        overflow: 'hidden'
      }}>
        <div style={{
          background: 'linear-gradient(135deg, #238548 0%, #97d6ac 100%)',
          color: '#ffffff',
          padding: '2rem',
          textAlign: 'center'
        }}>
          <h1 style={{
            margin: '0 0 0.5rem 0',
            fontSize: '2rem',
            fontWeight: 'bold',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '0.5rem'
          }}>
            <span>📈</span>
            Marks Review & Supply Exam
          </h1>
          <p style={{
            margin: 0,
            fontSize: '1.1rem',
            opacity: 0.9
          }}>
            Request marks review or apply for supplementary examinations
          </p>
        </div>

        {/* Tab Navigation */}
        <div style={{
          display: 'flex',
          borderBottom: '1px solid #ecf0f1'
        }}>
          {[
            { id: 'marks-review', label: 'Marks Review Request', icon: '🔍' },
            { id: 'supply-exam', label: 'Supply Exam Application', icon: '✏️' }
          ].map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              style={{
                flex: 1,
                background: activeTab === tab.id ? '#238548' : 'transparent',
                color: activeTab === tab.id ? '#ffffff' : '#666',
                border: 'none',
                padding: '1rem',
                cursor: 'pointer',
                fontSize: '1rem',
                fontWeight: '500',
                transition: 'all 0.3s',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '0.5rem'
              }}
            >
              <span>{tab.icon}</span>
              <span>{tab.label}</span>
            </button>
          ))}
        </div>
      </div>

      {/* Tab Content */}
      {activeTab === 'marks-review' ? (
        /* Marks Review Request */
        <div style={{
          background: '#ffffff',
          borderRadius: '12px',
          boxShadow: '0 4px 15px rgba(0,0,0,0.1)',
          overflow: 'hidden'
        }}>
          <div style={{
            background: '#238548',
            color: '#ffffff',
            padding: '1.5rem',
            display: 'flex',
            alignItems: 'center',
            gap: '1rem'
          }}>
            <span style={{ fontSize: '1.8rem' }}>🔍</span>
            <h2 style={{ margin: 0, fontSize: '1.3rem' }}>Request Marks Review</h2>
          </div>

          <div style={{
            background: '#d1ecf1',
            color: '#0c5460',
            padding: '1rem 1.5rem',
            fontSize: '0.95rem'
          }}>
            Submit a request for review of your marks card. Please fill in all required details.
          </div>

          <div style={{ padding: '2rem' }}>
            <form onSubmit={handleReviewSubmit}>
              <div style={{
                display: 'grid',
                gridTemplateColumns: isMobile ? '1fr' : 'repeat(2, 1fr)',
                gap: '1.5rem',
                marginBottom: '1.5rem'
              }}>
                {/* Semester */}
                <div>
                  <label style={{
                    display: 'block',
                    marginBottom: '0.5rem',
                    fontWeight: '500',
                    color: '#333'
                  }}>
                    Semester <span style={{ color: '#e74c3c' }}>*</span>
                  </label>
                  <select
                    value={reviewForm.semester}
                    onChange={(e) => setReviewForm(prev => ({ ...prev, semester: e.target.value, subject: '' }))}
                    required
                    style={{
                      width: '100%',
                      padding: '0.75rem',
                      border: '2px solid #ecf0f1',
                      borderRadius: '8px',
                      fontSize: '1rem',
                      outline: 'none',
                      transition: 'border-color 0.3s'
                    }}
                    onFocus={(e) => {
                      e.target.style.borderColor = '#238548';
                    }}
                    onBlur={(e) => {
                      e.target.style.borderColor = '#ecf0f1';
                    }}
                  >
                    <option value="">Select Semester</option>
                    {marksReviewData.availableSemesters.map((semData, index) => (
                      <option key={index} value={semData.semester}>
                        {semData.semester} ({semData.academicYear})
                      </option>
                    ))}
                  </select>
                </div>

                {/* Academic Year */}
                <div>
                  <label style={{
                    display: 'block',
                    marginBottom: '0.5rem',
                    fontWeight: '500',
                    color: '#333'
                  }}>
                    Academic Year <span style={{ color: '#e74c3c' }}>*</span>
                  </label>
                  <select
                    value={reviewForm.academicYear}
                    onChange={(e) => setReviewForm(prev => ({ ...prev, academicYear: e.target.value }))}
                    required
                    style={{
                      width: '100%',
                      padding: '0.75rem',
                      border: '2px solid #ecf0f1',
                      borderRadius: '8px',
                      fontSize: '1rem',
                      outline: 'none',
                      transition: 'border-color 0.3s'
                    }}
                    onFocus={(e) => {
                      e.target.style.borderColor = '#238548';
                    }}
                    onBlur={(e) => {
                      e.target.style.borderColor = '#ecf0f1';
                    }}
                  >
                    <option value="">Select Academic Year</option>
                    {[...new Set(marksReviewData.availableSemesters.map(s => s.academicYear))].map((year, index) => (
                      <option key={index} value={year}>
                        {year}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              {/* Subject */}
              <div style={{ marginBottom: '1.5rem' }}>
                <label style={{
                  display: 'block',
                  marginBottom: '0.5rem',
                  fontWeight: '500',
                  color: '#333'
                }}>
                  Subject (Optional, leave blank for full marks card review)
                </label>
                <select
                  value={reviewForm.subject}
                  onChange={(e) => setReviewForm(prev => ({ ...prev, subject: e.target.value }))}
                  style={{
                    width: '100%',
                    padding: '0.75rem',
                    border: '2px solid #ecf0f1',
                    borderRadius: '8px',
                    fontSize: '1rem',
                    outline: 'none',
                    transition: 'border-color 0.3s'
                  }}
                  onFocus={(e) => {
                    e.target.style.borderColor = '#238548';
                  }}
                  onBlur={(e) => {
                    e.target.style.borderColor = '#ecf0f1';
                  }}
                >
                  <option value="">Select Subject (Optional)</option>
                  {getAvailableSubjects().map((subject, index) => (
                    <option key={index} value={subject.value}>
                      {subject.label}
                    </option>
                  ))}
                </select>
              </div>

              {/* Reason for Review */}
              <div style={{ marginBottom: '2rem' }}>
                <label style={{
                  display: 'block',
                  marginBottom: '0.5rem',
                  fontWeight: '500',
                  color: '#333'
                }}>
                  Reason for Review <span style={{ color: '#e74c3c' }}>*</span>
                </label>
                <textarea
                  value={reviewForm.reason}
                  onChange={(e) => setReviewForm(prev => ({ ...prev, reason: e.target.value }))}
                  placeholder="Please explain why you are requesting a marks review..."
                  required
                  rows={4}
                  style={{
                    width: '100%',
                    padding: '0.75rem',
                    border: '2px solid #ecf0f1',
                    borderRadius: '8px',
                    fontSize: '1rem',
                    outline: 'none',
                    transition: 'border-color 0.3s',
                    resize: 'vertical'
                  }}
                  onFocus={(e) => {
                    e.target.style.borderColor = '#238548';
                  }}
                  onBlur={(e) => {
                    e.target.style.borderColor = '#ecf0f1';
                  }}
                />
              </div>

              {/* Submit Button */}
              <button
                type="submit"
                disabled={submitting}
                style={{
                  background: submitting ? '#95a5a6' : '#238548',
                  color: '#ffffff',
                  border: 'none',
                  padding: '1rem 2rem',
                  borderRadius: '8px',
                  fontSize: '1rem',
                  fontWeight: '500',
                  cursor: submitting ? 'not-allowed' : 'pointer',
                  transition: 'all 0.3s ease',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.5rem',
                  opacity: submitting ? 0.7 : 1
                }}
                onMouseEnter={(e) => {
                  if (!submitting) {
                    e.target.style.backgroundColor = '#1e7040';
                    e.target.style.transform = 'translateY(-2px)';
                  }
                }}
                onMouseLeave={(e) => {
                  if (!submitting) {
                    e.target.style.backgroundColor = '#238548';
                    e.target.style.transform = 'translateY(0)';
                  }
                }}
              >
                <span>{submitting ? '⏳' : '📤'}</span>
                {submitting ? 'Submitting...' : 'Submit Request'}
              </button>
            </form>
          </div>
        </div>
      ) : (
        /* Supply Exam Application */
        <div style={{
          background: '#ffffff',
          borderRadius: '12px',
          boxShadow: '0 4px 15px rgba(0,0,0,0.1)',
          overflow: 'hidden'
        }}>
          <div style={{
            background: '#238548',
            color: '#ffffff',
            padding: '1.5rem',
            display: 'flex',
            alignItems: 'center',
            gap: '1rem'
          }}>
            <span style={{ fontSize: '1.8rem' }}>✏️</span>
            <h2 style={{ margin: 0, fontSize: '1.3rem' }}>Apply for Supplementary Exam</h2>
          </div>

          <div style={{
            background: '#d1ecf1',
            color: '#0c5460',
            padding: '1rem 1.5rem',
            fontSize: '0.95rem'
          }}>
            Apply for supplementary exams for failed subjects. Select the semester and subjects you need to retake.
          </div>

          <div style={{ padding: '2rem' }}>
            <form onSubmit={handleSupplySubmit}>
              <div style={{
                display: 'grid',
                gridTemplateColumns: isMobile ? '1fr' : 'repeat(2, 1fr)',
                gap: '1.5rem',
                marginBottom: '1.5rem'
              }}>
                {/* Semester */}
                <div>
                  <label style={{
                    display: 'block',
                    marginBottom: '0.5rem',
                    fontWeight: '500',
                    color: '#333'
                  }}>
                    Semester <span style={{ color: '#e74c3c' }}>*</span>
                  </label>
                  <select
                    value={supplyForm.semester}
                    onChange={(e) => setSupplyForm(prev => ({ ...prev, semester: e.target.value }))}
                    required
                    style={{
                      width: '100%',
                      padding: '0.75rem',
                      border: '2px solid #ecf0f1',
                      borderRadius: '8px',
                      fontSize: '1rem',
                      outline: 'none',
                      transition: 'border-color 0.3s'
                    }}
                    onFocus={(e) => {
                      e.target.style.borderColor = '#238548';
                    }}
                    onBlur={(e) => {
                      e.target.style.borderColor = '#ecf0f1';
                    }}
                  >
                    <option value="">Select Semester</option>
                    {marksReviewData.availableSemesters.map((semData, index) => (
                      <option key={index} value={semData.semester}>
                        {semData.semester} ({semData.academicYear})
                      </option>
                    ))}
                  </select>
                </div>

                {/* Academic Year */}
                <div>
                  <label style={{
                    display: 'block',
                    marginBottom: '0.5rem',
                    fontWeight: '500',
                    color: '#333'
                  }}>
                    Academic Year <span style={{ color: '#e74c3c' }}>*</span>
                  </label>
                  <select
                    value={supplyForm.academicYear}
                    onChange={(e) => setSupplyForm(prev => ({ ...prev, academicYear: e.target.value }))}
                    required
                    style={{
                      width: '100%',
                      padding: '0.75rem',
                      border: '2px solid #ecf0f1',
                      borderRadius: '8px',
                      fontSize: '1rem',
                      outline: 'none',
                      transition: 'border-color 0.3s'
                    }}
                    onFocus={(e) => {
                      e.target.style.borderColor = '#238548';
                    }}
                    onBlur={(e) => {
                      e.target.style.borderColor = '#ecf0f1';
                    }}
                  >
                    <option value="">Select Academic Year</option>
                    {[...new Set(marksReviewData.availableSemesters.map(s => s.academicYear))].map((year, index) => (
                      <option key={index} value={year}>
                        {year}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              {/* Select Failed Subjects */}
              <div style={{ marginBottom: '2rem' }}>
                <label style={{
                  display: 'block',
                  marginBottom: '0.5rem',
                  fontWeight: '500',
                  color: '#333'
                }}>
                  Select Failed Subjects <span style={{ color: '#e74c3c' }}>*</span>
                </label>

                {!supplyForm.semester || !supplyForm.academicYear ? (
                  <div style={{
                    padding: '2rem',
                    border: '2px solid #ecf0f1',
                    borderRadius: '8px',
                    textAlign: 'center',
                    color: '#666',
                    backgroundColor: '#f8f9fa'
                  }}>
                    Please select semester and academic year to view failed subjects.
                  </div>
                ) : getAvailableFailedSubjects().length === 0 ? (
                  <div style={{
                    padding: '2rem',
                    border: '2px solid #e8f5e9',
                    borderRadius: '8px',
                    textAlign: 'center',
                    color: '#2e7d32',
                    backgroundColor: '#f1f8e9'
                  }}>
                    <div style={{ fontSize: '2rem', marginBottom: '0.5rem' }}>🎉</div>
                    <strong>Great! No failed subjects found for this semester.</strong>
                    <br />
                    You have successfully passed all subjects.
                  </div>
                ) : (
                  <div style={{
                    border: '2px solid #ecf0f1',
                    borderRadius: '8px',
                    padding: '1rem'
                  }}>
                    {getAvailableFailedSubjects().map((subject) => (
                      <label key={subject.subjectCode} style={{
                        display: 'flex',
                        alignItems: 'center',
                        padding: '0.75rem',
                        margin: '0.25rem 0',
                        backgroundColor: supplyForm.failedSubjects.includes(subject.subjectCode) ? '#e8f5e9' : '#f8f9fa',
                        borderRadius: '6px',
                        cursor: 'pointer',
                        transition: 'background-color 0.3s'
                      }}>
                        <input
                          type="checkbox"
                          checked={supplyForm.failedSubjects.includes(subject.subjectCode)}
                          onChange={() => handleFailedSubjectToggle(subject.subjectCode)}
                          style={{
                            marginRight: '1rem',
                            transform: 'scale(1.2)'
                          }}
                        />
                        <div style={{ flex: 1 }}>
                          <div style={{ fontWeight: '500', color: '#333' }}>
                            {subject.subjectName}
                          </div>
                          <div style={{ fontSize: '0.85rem', color: '#666' }}>
                            Code: {subject.subjectCode} • Current Grade:
                            <span style={{
                              color: subject.grade === 'F' ? '#e74c3c' : '#f39c12',
                              fontWeight: 'bold',
                              marginLeft: '0.25rem'
                            }}>
                              {subject.grade}
                            </span>
                          </div>
                        </div>
                      </label>
                    ))}
                  </div>
                )}
              </div>

              {/* Submit Button */}
              <button
                type="submit"
                disabled={submitting || getAvailableFailedSubjects().length === 0}
                style={{
                  background: (submitting || getAvailableFailedSubjects().length === 0) ? '#95a5a6' : '#238548',
                  color: '#ffffff',
                  border: 'none',
                  padding: '1rem 2rem',
                  borderRadius: '8px',
                  fontSize: '1rem',
                  fontWeight: '500',
                  cursor: (submitting || getAvailableFailedSubjects().length === 0) ? 'not-allowed' : 'pointer',
                  transition: 'all 0.3s ease',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.5rem',
                  opacity: (submitting || getAvailableFailedSubjects().length === 0) ? 0.6 : 1
                }}
                onMouseEnter={(e) => {
                  if (!submitting && getAvailableFailedSubjects().length > 0) {
                    e.target.style.backgroundColor = '#1e7040';
                    e.target.style.transform = 'translateY(-2px)';
                  }
                }}
                onMouseLeave={(e) => {
                  if (!submitting && getAvailableFailedSubjects().length > 0) {
                    e.target.style.backgroundColor = '#238548';
                    e.target.style.transform = 'translateY(0)';
                  }
                }}
              >
                <span>{submitting ? '⏳' : '📋'}</span>
                {submitting ? 'Submitting...' : 'Submit Application'}
              </button>
            </form>
          </div>
        </div>
      )}

      {/* Previous Requests/Applications History */}
      {(marksReviewData.previousReviewRequests?.length > 0 || marksReviewData.previousSupplyApplications?.length > 0) && (
        <div style={{
          background: '#ffffff',
          borderRadius: '12px',
          boxShadow: '0 4px 15px rgba(0,0,0,0.1)',
          marginTop: '2rem',
          overflow: 'hidden'
        }}>
          <div style={{
            background: '#238548',
            color: '#ffffff',
            padding: '1.5rem',
            display: 'flex',
            alignItems: 'center',
            gap: '1rem'
          }}>
            <span style={{ fontSize: '1.8rem' }}>📋</span>
            <h3 style={{ margin: 0, fontSize: '1.2rem' }}>Previous Requests & Applications</h3>
          </div>
          <div style={{ padding: '1.5rem' }}>
            {/* Review Requests */}
            {marksReviewData.previousReviewRequests?.length > 0 && (
              <div style={{ marginBottom: '2rem' }}>
                <h4 style={{ margin: '0 0 1rem 0', color: '#333' }}>Marks Review Requests</h4>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
                  {marksReviewData.previousReviewRequests.slice(0, 5).map((request, index) => (
                    <div key={index} style={{
                      padding: '1rem',
                      background: '#f8f9fa',
                      borderRadius: '8px',
                      border: '1px solid #e9ecef'
                    }}>
                      <div style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        marginBottom: '0.5rem'
                      }}>
                        <span style={{ fontWeight: '600', color: '#333' }}>
                          {request.semester} - {request.academicYear}
                        </span>
                        <span style={{
                          padding: '0.25rem 0.75rem',
                          borderRadius: '15px',
                          fontSize: '0.8rem',
                          fontWeight: '500',
                          backgroundColor: request.status === 'pending' ? '#fff3cd' :
                            request.status === 'completed' ? '#d1e7dd' : '#ffeaa7',
                          color: request.status === 'pending' ? '#856404' :
                            request.status === 'completed' ? '#0f5132' : '#6c5ce7'
                        }}>
                          {request.status}
                        </span>
                      </div>
                      <div style={{ fontSize: '0.9rem', color: '#666' }}>
                        Subject: {request.subject || 'Full Marks Card'} |
                        Submitted: {formatDate(request.date)}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Supply Applications */}
            {marksReviewData.previousSupplyApplications?.length > 0 && (
              <div>
                <h4 style={{ margin: '0 0 1rem 0', color: '#333' }}>Supply Exam Applications</h4>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
                  {marksReviewData.previousSupplyApplications.slice(0, 5).map((app, index) => (
                    <div key={index} style={{
                      padding: '1rem',
                      background: '#f8f9fa',
                      borderRadius: '8px',
                      border: '1px solid #e9ecef'
                    }}>
                      <div style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        marginBottom: '0.5rem'
                      }}>
                        <span style={{ fontWeight: '600', color: '#333' }}>
                          {app.semester} - {app.academicYear}
                        </span>
                        <span style={{
                          padding: '0.25rem 0.75rem',
                          borderRadius: '15px',
                          fontSize: '0.8rem',
                          fontWeight: '500',
                          backgroundColor: app.status === 'pending' ? '#fff3cd' :
                            app.status === 'approved' ? '#d1e7dd' : '#f8d7da',
                          color: app.status === 'pending' ? '#856404' :
                            app.status === 'approved' ? '#0f5132' : '#721c24'
                        }}>
                          {app.status}
                        </span>
                      </div>
                      <div style={{ fontSize: '0.9rem', color: '#666' }}>
                        Subject: {app.subject} - {app.subjectName} |
                        Submitted: {formatDate(app.date)}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </Layout>
  );
};

export default StudentMarksReview;